import React from "react";
import { ChevronDown, ChevronUp, Pause, Play, X, RotateCcw, KeyRound, FolderOpen, Upload } from "lucide-react";
import { S, LABEL } from "@/lib/antreanUnggah";
import { ukuranTeks, kecepatanTeks, sisaWaktuTeks } from "@/lib/fileMaster";
import { fileDariSimpanan } from "@/lib/pilihFile";

const TOMBOL = "inline-flex items-center gap-1 rounded border px-2 py-0.5 text-[11px] disabled:opacity-50";
const AKTIF = [S.PERIKSA, S.UNGGAH, S.SATUKAN];
const URUT = { PERIKSA: 0, UNGGAH: 0, SATUKAN: 0, ANTRE: 1, JEDA: 2, GAGAL: 2, PERLU_IZIN: 3, PERLU_FILE: 3, DITOLAK: 4, SELESAI: 5, DILEWATI: 6, BATAL: 7 };
const WARNA = {
  SELESAI: "text-emerald-300", DITOLAK: "text-red-300", GAGAL: "text-red-300", DILEWATI: "text-slate-400",
  BATAL: "text-slate-400", JEDA: "text-amber-300", PERLU_IZIN: "text-amber-300", PERLU_FILE: "text-amber-300",
};
const KUNCI_KECIL = "soq_unggah_kecil";
const bacaKecil = () => { try { return localStorage.getItem(KUNCI_KECIL) === "1"; } catch { return false; } };
const persen = (x) => (x.total ? Math.min(100, Math.floor((100 * (x.byte || 0)) / x.total)) : 0);

// v2.22.2: the floating upload panel (bottom right; full width on a phone). It stays while the user changes menu.
export default function PanelUnggahan({ antrean, daftar }) {
  const [kecil, setKecil] = React.useState(bacaKecil);
  const [pesan, setPesan] = React.useState(null);
  const pilihUlang = React.useRef(null);
  const ubahKecil = (v) => { setKecil(v); try { localStorage.setItem(KUNCI_KECIL, v ? "1" : "0"); } catch { /* not kept */ } };

  const r = antrean.ringkasan();
  const jalan = daftar.find((x) => AKTIF.includes(x.status));
  const perluIzin = daftar.filter((x) => x.status === S.PERLU_IZIN);
  const perluFile = daftar.filter((x) => x.status === S.PERLU_FILE);
  const urut = [...daftar].sort((a, b) => (URUT[a.status] ?? 9) - (URUT[b.status] ?? 9));
  const dikerjakan = r.total - r.dilewati; // rows that were (or will be) sent
  const beres = r.selesai + r.ditolak + r.batal;
  const tenang = r.sisa === 0 && r.jeda === 0 && r.gagal === 0 && r.perlu === 0;

  const izinkan = async (ids) => {
    setPesan(null);
    const sudah = [];
    for (const id of ids) {
      const it = antrean.cari(id);
      if (!it) continue;
      try {
        const f = await fileDariSimpanan(it, sudah);
        if (f) antrean.pasangFile(id, f);
        else antrean.perluFile(id, "Izin belum diberikan. Tekan Izinkan lagi, atau pilih file yang sama.");
      } catch (e) {
        antrean.perluFile(id, e?.name === "NotFoundError"
          ? "File tidak ditemukan lagi di tempat semula. Pilih file yang sama."
          : "Tekan Izinkan pada baris ini (browser meminta izin satu per satu).");
      }
    }
  };
  const cocokkan = (files) => {
    const n = antrean.cocokkanFile(files);
    const sisa = antrean.daftar().filter((x) => [S.PERLU_FILE, S.PERLU_IZIN].includes(x.status)).length;
    setPesan(n ? `${n} file cocok dan dilanjutkan.${sisa ? ` ${sisa} lagi belum cocok (nama atau ukuran berbeda).` : ""}`
      : "Tidak ada yang cocok. Pilih file dengan nama dan ukuran yang sama.");
  };
  const lupakan = async () => {
    if (!window.confirm("Batalkan semua unggahan dari sesi sebelumnya? Bagian yang sudah terkirim dibuang.")) return;
    for (const x of [...perluIzin, ...perluFile]) await antrean.batal(x.id);
  };

  const judul = jalan ? `${jalan.nama} · ${persen(jalan)}%`
    : r.perlu ? `${r.perlu} unggahan belum selesai` : r.sisa ? "Menunggu giliran" : tenang ? "Selesai" : "Berhenti";

  return (
    <div data-testid="unggah-panel" role="region" aria-label="Unggahan file master"
      className="fixed inset-x-2 bottom-2 z-40 rounded-lg border border-slate-700 bg-slate-900 text-sm shadow-xl sm:inset-x-auto sm:right-4 sm:bottom-4 sm:w-[26rem]">
      <div className="flex items-center gap-2 border-b border-slate-800 px-3 py-2">
        <Upload size={14} className="shrink-0 text-amber-400" />
        <div className="min-w-0 flex-1">
          <div className="text-xs font-semibold text-slate-200">
            Unggahan file master <span data-testid="unggah-hitung" className="font-normal text-slate-400">· {beres} dari {dikerjakan} selesai</span>
          </div>
          <div data-testid="unggah-judul" className="truncate text-[11px] text-slate-400">{judul}</div>
        </div>
        <button data-testid="unggah-kecil" type="button" onClick={() => ubahKecil(!kecil)} aria-label={kecil ? "Perbesar" : "Perkecil"}
          className="rounded p-1 text-slate-400 hover:bg-slate-800">{kecil ? <ChevronUp size={16} /> : <ChevronDown size={16} />}</button>
        {tenang && (
          <button data-testid="unggah-tutup" type="button" onClick={() => antrean.bersihkan()} aria-label="Tutup"
            className="rounded p-1 text-slate-400 hover:bg-slate-800"><X size={16} /></button>)}
      </div>
      {jalan && <div className="h-1 w-full bg-slate-800"><div className="h-full bg-amber-500" style={{ width: `${persen(jalan)}%` }} /></div>}

      {!kecil && (
        <div className="space-y-2 p-2">
          {r.perlu > 0 && (
            <div data-testid="unggah-pulih" className="space-y-1.5 rounded border border-amber-500/40 bg-amber-500/5 p-2 text-xs text-slate-200">
              <div>Ada {r.perlu} unggahan yang belum selesai dari sesi sebelumnya. Bagian yang sudah terkirim tidak dikirim ulang.</div>
              <div className="flex flex-wrap gap-1">
                {perluIzin.length > 0 && (
                  <button data-testid="unggah-izinkan" type="button" onClick={() => izinkan(perluIzin.map((x) => x.id))}
                    className={`${TOMBOL} border-amber-500/60 text-slate-100`}><KeyRound size={12} /> Lanjutkan unggahan (Izinkan)</button>)}
                {(perluFile.length > 0 || perluIzin.length > 0) && (
                  <button data-testid="unggah-pilih-ulang" type="button" onClick={() => pilihUlang.current?.click()}
                    className={`${TOMBOL} border-slate-600 text-slate-200`}><FolderOpen size={12} /> {perluIzin.length ? "Atau pilih file yang sama" : "Lanjutkan unggahan: pilih file yang sama"}</button>)}
                <button data-testid="unggah-lupakan" type="button" onClick={lupakan} className={`${TOMBOL} border-slate-700 text-slate-400`}>Batalkan semuanya</button>
              </div>
              <input ref={pilihUlang} data-testid="unggah-pilih-ulang-file" type="file" multiple className="hidden"
                onChange={(e) => { const fs = Array.from(e.target.files || []); e.target.value = ""; if (fs.length) cocokkan(fs); }} />
            </div>
          )}
          {tenang && beres + r.dilewati > 0 && (
            <div data-testid="unggah-ringkasan" className="rounded border border-slate-700 px-2 py-1.5 text-xs text-slate-200">
              Selesai: <b className="text-emerald-300">{r.selesai} berhasil</b>
              {r.ditolak > 0 && <> · <b className="text-red-300">{r.ditolak} ditolak</b></>}
              {r.dilewati > 0 && <> · {r.dilewati} dilewati</>}
              {r.batal > 0 && <> · {r.batal} dibatalkan</>}
              {(r.ditolak > 0 || r.dilewati > 0) && <span className="text-slate-400"> — alasannya di bawah.</span>}
            </div>
          )}
          {pesan && <div data-testid="unggah-pesan" className="text-xs text-slate-300">{pesan}</div>}
          <ul data-testid="unggah-daftar" className="max-h-[45vh] divide-y divide-slate-800 overflow-y-auto overscroll-contain rounded border border-slate-800">
            {urut.map((x) => {
              const aktif = AKTIF.includes(x.status);
              const kec = x.status === S.UNGGAH ? kecepatanTeks(x.byteSesi, x.msSesi) : null;
              const sisa = x.status === S.UNGGAH ? sisaWaktuTeks((x.total || 0) - (x.byte || 0), x.byteSesi, x.msSesi) : null;
              return (
                <li key={x.id} data-testid="unggah-baris" data-status={x.status} data-nama={x.nama} className="space-y-1 px-2 py-1.5">
                  <div className="flex items-start gap-2">
                    <div className="min-w-0 flex-1">
                      <div className="truncate text-xs text-slate-200" title={x.jalur || x.nama}>{x.nama}</div>
                      <div className="flex flex-wrap gap-x-2 text-[11px] text-slate-500">
                        {x.spkLabel && <span>{x.spkLabel}</span>}
                        <span>{ukuranTeks(x.ukuran)}</span>
                        <span data-testid="unggah-status" className={WARNA[x.status] || "text-slate-300"}>
                          {x.menjeda ? "Menjeda… (menunggu bagian yang sedang terkirim)" : x.status === S.UNGGAH && x.lanjut ? "Melanjutkan…" : LABEL[x.status]}
                          {x.status === S.SELESAI && x.versi ? ` · v${x.versi} (draf)` : ""}
                          {(aktif || x.status === S.JEDA) && x.total ? ` · ${persen(x)}%` : ""}
                        </span>
                        {kec && <span>{kec}</span>}
                        {sisa && <span>sisa {sisa}</span>}
                      </div>
                    </div>
                    <div className="flex shrink-0 gap-1">
                      {[S.PERIKSA, S.UNGGAH].includes(x.status) && !x.menjeda && (
                        <button data-testid="unggah-jeda" type="button" onClick={() => antrean.jeda(x.id)} className={`${TOMBOL} border-slate-600 text-slate-200`} title="Jeda"><Pause size={11} /> Jeda</button>)}
                      {x.status === S.ANTRE && (
                        <button data-testid="unggah-tahan" type="button" onClick={() => antrean.jeda(x.id)} className={`${TOMBOL} border-slate-700 text-slate-400`} title="Jeda"><Pause size={11} /></button>)}
                      {[S.JEDA, S.GAGAL].includes(x.status) && (
                        <button data-testid="unggah-lanjut" type="button" onClick={() => antrean.lanjut(x.id)} className={`${TOMBOL} border-amber-500/50 text-slate-100`}>
                          {x.status === S.GAGAL ? <><RotateCcw size={11} /> Coba lagi</> : <><Play size={11} /> Lanjut</>}</button>)}
                      {x.status === S.PERLU_IZIN && (
                        <button data-testid="unggah-izinkan-satu" type="button" onClick={() => izinkan([x.id])} className={`${TOMBOL} border-amber-500/50 text-slate-100`}><KeyRound size={11} /> Izinkan</button>)}
                      {![S.SELESAI, S.DITOLAK, S.DILEWATI, S.BATAL, S.SATUKAN].includes(x.status) && (
                        <button data-testid="unggah-batal" type="button" onClick={() => antrean.batal(x.id)} className={`${TOMBOL} border-slate-700 text-slate-400`} title="Batalkan file ini"><X size={11} /></button>)}
                    </div>
                  </div>
                  {aktif && <div className="h-1.5 w-full overflow-hidden rounded bg-slate-800"><div className="h-full bg-amber-500" style={{ width: `${persen(x)}%` }} /></div>}
                  {x.alasan && <div data-testid="unggah-alasan" className={`text-[11px] ${[S.DITOLAK, S.GAGAL].includes(x.status) ? "text-red-300" : "text-slate-400"}`}>{x.alasan}</div>}
                </li>
              );
            })}
          </ul>
          {r.sisa > 0 && <p className="text-[11px] text-slate-500">Boleh pindah menu: unggahan tetap berjalan. Jangan tutup tab atau browser sampai selesai.</p>}
        </div>
      )}
    </div>
  );
}
